import { gql, useMutation } from "@apollo/client";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import { Avatar, Button, Dialog, DialogActions, Slide, Typography } from "@mui/material";
import type { TransitionProps } from "@mui/material/transitions";
import Cookies from "js-cookie";
import React, { useEffect, useRef, useState } from "react";

interface Props {
  children: React.ReactNode;
}

const IDLE_LIMIT_MS = 60 * 60 * 1000;
const THROTTLE_MS = 1_000;
const ACTIVITY_BROADCAST_KEY = "lastActivityTimestamp";

const EVENTS: (keyof WindowEventMap)[] = [
  "mousemove",
  "mousedown",
  "click",
  "scroll",
  "keydown",
  "touchstart",
];

const EXPIRE_REFRESH_TOKEN = gql`
  mutation expireRefreshToken($userId: String!) {
    expireRefreshToken(data: { userId: $userId }) {
      done
    }
  }
`;

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>
) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const isLoggedIn = () => {
  const path = window.location.pathname;
  return path !== "/login" && path !== "/logout";
};

const AppLogout: React.FC<Props> = ({ children }) => {
  const [dialogOpen, setDialogOpen] = useState(false);

  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const lastEvtRef = useRef<number>(Date.now());
  const lastActivityAt = useRef<number>(Date.now());
  const hiddenAt = useRef<number | null>(null);

  const [expireRefreshToken] = useMutation(EXPIRE_REFRESH_TOKEN);

  const invalidateSession = async () => {
    try {
      const userId = Cookies.get("JWT_USERNAME_COOKIE");
      if (userId) {
        await expireRefreshToken({ variables: { userId } });
      }
    } catch {
      /* silent */
    } finally {
      const logoutEvent = localStorage.getItem("logout");
      localStorage.removeItem(ACTIVITY_BROADCAST_KEY);
      localStorage.clear();
      if (logoutEvent) {
        localStorage.setItem("logout", logoutEvent);
      }
      Cookies.remove("JWT_ACCESS_TOKEN");
    }
  };
  const performLogout = async () => {
    localStorage.setItem("logout", Date.now().toString());
    await invalidateSession();
    window.location.href = "/logout";
  };

  const restartTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    if (!isLoggedIn()) {
      return;
    }

    // Check shared activity timestamp to calculate remaining time
    const now = Date.now();
    let sharedActivityTime = 0;
    try {
      const sharedActivityStr = localStorage.getItem(ACTIVITY_BROADCAST_KEY);
      sharedActivityTime = sharedActivityStr ? parseInt(sharedActivityStr) : 0;
    } catch (e) {
      // localStorage might be unavailable in some environments
      sharedActivityTime = 0;
    }

    // Use the most recent activity time
    const lastActivity = Math.max(lastActivityAt.current, sharedActivityTime);
    const timeSinceLastActivity = now - lastActivity;
    const remainingTime = Math.max(0, IDLE_LIMIT_MS - timeSinceLastActivity);

    timerRef.current = setTimeout(() => {
      // Double-check with shared activity before showing dialog
      let currentSharedActivity = 0;
      try {
        const currentSharedActivityStr = localStorage.getItem(ACTIVITY_BROADCAST_KEY);
        currentSharedActivity = currentSharedActivityStr ? parseInt(currentSharedActivityStr) : 0;
      } catch (e) {
        // localStorage might be unavailable in some environments
        currentSharedActivity = 0;
      }
      const finalLastActivity = Math.max(lastActivityAt.current, currentSharedActivity);

      if (Date.now() - finalLastActivity >= IDLE_LIMIT_MS && isLoggedIn()) {
        setDialogOpen(true);
      } else {
        // Activity happened in another tab, restart the timer
        restartTimer();
      }
    }, remainingTime);
  };

  const activityHandler = () => {
    if (dialogOpen || !isLoggedIn()) {
      return;
    }
    const now = Date.now();
    if (now - lastEvtRef.current < THROTTLE_MS) {
      return;
    }
    lastEvtRef.current = now;
    lastActivityAt.current = now;
    localStorage.setItem(ACTIVITY_BROADCAST_KEY, now.toString());
    restartTimer();
  };

  const visibilityHandler = () => {
    if (!isLoggedIn()) {
      return;
    }
    if (document.hidden) {
      hiddenAt.current = Date.now();
    } else {
      const now = Date.now();

      // Check shared activity timestamp from localStorage
      let sharedActivityTime = 0;
      try {
        const sharedActivityStr = localStorage.getItem(ACTIVITY_BROADCAST_KEY);
        sharedActivityTime = sharedActivityStr ? parseInt(sharedActivityStr) : 0;
      } catch (e) {
        // localStorage might be unavailable in some environments
        sharedActivityTime = 0;
      }

      // Use the most recent activity across all tabs
      const lastRelevant = Math.max(
        lastActivityAt.current,
        hiddenAt.current ?? 0,
        sharedActivityTime
      );

      if (now - lastRelevant >= IDLE_LIMIT_MS) {
        if (isLoggedIn()) {
          setDialogOpen(true);
        }
      }

      hiddenAt.current = null;
    }
  };

  useEffect(() => {
    if (!isLoggedIn()) {
      return;
    }

    // Initialize lastActivityAt with shared activity if available
    try {
      const sharedActivityStr = localStorage.getItem(ACTIVITY_BROADCAST_KEY);
      if (sharedActivityStr) {
        const sharedActivityTime = parseInt(sharedActivityStr);
        if (!isNaN(sharedActivityTime)) {
          lastActivityAt.current = Math.max(lastActivityAt.current, sharedActivityTime);
        }
      }
    } catch (e) {
      // localStorage might be unavailable in some environments
    }

    const handlePopState = () => {
      if (dialogOpen) {
        performLogout();
      }
    };

    const handlePushState = () => {
      if (dialogOpen) {
        performLogout();
      }
    };

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === ACTIVITY_BROADCAST_KEY && e.newValue && !dialogOpen) {
        const activityTime = parseInt(e.newValue);
        if (!isNaN(activityTime)) {
          lastActivityAt.current = activityTime;
          restartTimer();
        }
      }
    };

    window.addEventListener("popstate", handlePopState);
    window.addEventListener("pushstate", handlePushState);
    window.addEventListener("storage", handleStorageChange);

    restartTimer();
    EVENTS.forEach((evt) => window.addEventListener(evt, activityHandler, { passive: true }));
    document.addEventListener("visibilitychange", visibilityHandler);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      EVENTS.forEach((evt) => window.removeEventListener(evt, activityHandler));
      document.removeEventListener("visibilitychange", visibilityHandler);
      window.removeEventListener("popstate", handlePopState);
      window.removeEventListener("pushstate", handlePushState);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [dialogOpen]);

  useEffect(() => {
    const beforeUnload = () => {
      if (dialogOpen) {
        invalidateSession();
        sessionStorage.setItem("forceLogout", "1");
      }
    };

    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
  }, [dialogOpen]);

  return (
    <>
      {children}

      <Dialog
        open={dialogOpen}
        TransitionComponent={Transition}
        keepMounted
        disableEscapeKeyDown
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        onClose={() => {}}
        PaperProps={{
          sx: {
            borderRadius: 4,
            px: 4,
            py: 3,
            textAlign: "center",
            width: { xs: "90%", sm: 420 },
          },
          elevation: 8,
        }}
      >
        <Avatar
          sx={{
            bgcolor: "primary.main",
            width: 64,
            height: 64,
            mx: "auto",
            mb: 2,
          }}
        >
          <AccessTimeRoundedIcon fontSize="large" />
        </Avatar>

        <Typography variant="h6" fontWeight={600} gutterBottom>
          Session Expired
        </Typography>

        <Typography variant="body1" sx={{ mb: 3 }}>
          You’ve been away for a while and your secure session has ended.
          <br />
          Press <strong>OK</strong> to sign in again.
        </Typography>

        <DialogActions sx={{ justifyContent: "center", px: 0 }}>
          <Button
            className="button"
            variant="contained"
            size="large"
            onClick={performLogout}
            sx={{ minWidth: 140 }}
            autoFocus
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AppLogout;
